import * as api from "../api";
import { cachedFetch, type CompactAccount, type Found, compactAccount, dayKeys, FETCH_WAIT_MS, isNetworkError, nearbyLocal, withWait } from "./cache";
import type { Scope } from "./db";
import { localVisitDefaults } from "./local";
import { entries } from "./outbox";
import { ROUTE_DAY_GET, shareFetch } from "./share";

type Token = () => Promise<string>;
export type DayLoads = Awaited<ReturnType<typeof api.loads>>;

/** `at` is the time of the stored copy when the phone had no signal. `fresh` is the answer that can come after the copy. */
export interface TodayData {
  day: api.RouteDay;
  loads: DayLoads | null;
  at: number | null;
  fresh?: Promise<TodayData>;
}

const markOnline = (scope: Scope) => void scope.write(dayKeys.online, Date.now());

const loadsOf = (scope: Scope, token: Token, day: string) =>
  cachedFetch(scope.key(dayKeys.loads(day)), async () => api.loads(await token(), day), { keptAfter: null }).then(
    (found) => found.value,
    () => null,
  );

/** `today` is the day of the organization on this phone; the server's day names the copy. */
export async function loadToday(scope: Scope, token: Token, today: string): Promise<TodayData> {
  const found = await cachedFetch(scope.key(dayKeys.route(today)), async () => api.routeDay(await token()), {
    saveAs: (day) => scope.key(dayKeys.route(day.today)),
  });
  const settle = async ({ value, at }: Found<api.RouteDay>): Promise<TodayData> => {
    if (at === null) markOnline(scope);
    return { day: value, loads: await loadsOf(scope, token, value.today), at };
  };
  if (!found.fresh) return settle(found);
  const loads = (await scope.read<DayLoads>(dayKeys.loads(found.value.today)))?.value ?? null;
  return { day: found.value, loads, at: found.at, fresh: found.fresh.then(settle) };
}

/** Sync gets the route day one time for each page load and keeps it on the phone as Today does (SO-1116). A second call gets the same answer. */
export function syncDayLoader(scope: Scope, token: Token): (today: string, rows: number) => Promise<Found<api.RouteDay> | null> {
  let pending: Promise<Found<api.RouteDay> | null> | null = null;
  const load = async (today: string, rows: number) => {
    const started = Date.now();
    try {
      const get = async () => shareFetch(scope.key(ROUTE_DAY_GET), async () => api.routeDay(await token()));
      const found = await cachedFetch(scope.key(dayKeys.route(today)), get, {
        saveAs: (day) => scope.key(dayKeys.route(day.today)),
        keptAfter: null,
      });
      if (found.at === null) markOnline(scope);
      console.info(`sync: route day for ${rows} rows: ${found.at === null ? "from the server" : "the copy on the phone"} in ${Date.now() - started} ms`);
      return found;
    } catch (e) {
      console.warn(`sync: route day for ${rows} rows failed in ${Date.now() - started} ms: ${e instanceof Error ? e.message : String(e)}; the rows use the day on the phone`);
      return null;
    }
  };
  return (today, rows) => (pending ??= load(today, rows));
}

export interface VisitData {
  defaults: api.VisitDefaults;
  loads: DayLoads | null;
  at: number | null;
  fresh?: Promise<VisitData>;
}

/** The kept defaults, with the next stop and the pre-trip state of the kept day and the entries on the phone. */
export async function keptVisitDefaults(scope: Scope, day: string, defaults: api.VisitDefaults): Promise<api.VisitDefaults> {
  const [route, all] = await Promise.all([scope.read<api.RouteDay>(dayKeys.route(day)), entries(scope)]);
  const found = localVisitDefaults(defaults, route?.value ?? null, all, route?.at ?? 0);
  if (found.next_stop?.route_stop_id !== defaults.next_stop?.route_stop_id || found.pre_trip_needed !== defaults.pre_trip_needed)
    console.info(`visit defaults: from the kept day and ${all.length} phone entries; pre-trip needed ${Boolean(found.pre_trip_needed)}`);
  return found;
}

export async function loadVisit(scope: Scope, token: Token, day: string): Promise<VisitData> {
  const loads = loadsOf(scope, token, day);
  const found = await cachedFetch(scope.key(dayKeys.defaults), async () => api.visitDefaults(await token()));
  const settle = async ({ value, at }: Found<api.VisitDefaults>): Promise<VisitData> => {
    if (at === null) markOnline(scope);
    return { defaults: at === null ? value : await keptVisitDefaults(scope, day, value), loads: await loads, at };
  };
  if (!found.fresh) return settle(found);
  const kept = (await scope.read<DayLoads>(dayKeys.loads(day)))?.value ?? null;
  return { defaults: await keptVisitDefaults(scope, day, found.value), loads: kept, at: found.at, fresh: found.fresh.then(settle) };
}

/** Get the visit defaults again and keep them, for example after a route start is sent. */
export async function refreshVisitDefaults(scope: Scope, token: Token): Promise<boolean> {
  try {
    await scope.write(dayKeys.defaults, await withWait(token().then(api.visitDefaults), FETCH_WAIT_MS));
    console.info("visit defaults: kept again");
    return true;
  } catch (e) {
    console.info(`visit defaults: not kept again (${isNetworkError(e) ? "no signal" : `status ${(e as api.ApiError).status}`})`);
    return false;
  }
}

export async function accountList(scope: Scope, token: Token): Promise<CompactAccount[]> {
  try {
    const list = (await withWait(token().then(api.accounts), FETCH_WAIT_MS)).map(compactAccount);
    await scope.write(dayKeys.accounts, list);
    return list;
  } catch (e) {
    const kept = isNetworkError(e) ? await scope.read<CompactAccount[]>(dayKeys.accounts) : null;
    if (!kept) throw e;
    return kept.value;
  }
}

/** Rule V-19: the visit labels, kept for a phone with no signal. Null: an older API or no copy, so only the note box shows. */
export async function visitLabelList(scope: Scope, token: Token): Promise<api.VisitLabels | null> {
  try {
    const found = await withWait(token().then(api.visitLabels), FETCH_WAIT_MS);
    await scope.write(dayKeys.labels, found);
    return found;
  } catch (e) {
    const kept = isNetworkError(e) ? await scope.read<api.VisitLabels>(dayKeys.labels) : null;
    console.info(`visit labels: ${kept ? "from the phone" : "none"} (${isNetworkError(e) ? "no signal" : `status ${(e as api.ApiError).status ?? "?"}`})`);
    return kept?.value ?? null;
  }
}

export async function keptAccount(scope: Scope, id: string): Promise<CompactAccount | null> {
  return (await scope.read<CompactAccount[]>(dayKeys.accounts))?.value.find((account) => account.id === id) ?? null;
}

/** The server's nearby list, or the nearest pins on the phone when there is no signal. */
export async function nearbyList(scope: Scope, token: Token, at: { lat: number; lng: number }): Promise<api.NearbyAccount[]> {
  try {
    return (await withWait(token().then((t) => api.nearbyAccounts(t, at.lat, at.lng)), FETCH_WAIT_MS)).accounts;
  } catch (e) {
    const kept = isNetworkError(e) ? await scope.read<CompactAccount[]>(dayKeys.accounts) : null;
    if (!kept) throw e;
    return nearbyLocal(kept.value, at);
  }
}
